import React, { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";

// Require Editor CSS files.
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";
import "froala-editor/css/plugins/char_counter.min.css";

import "froala-editor/js/plugins/word_paste.min.js";
import "froala-editor/js/plugins/char_counter.min.js";
import "froala-editor/js/plugins/inline_style.min.js";

// beyondgrammar plugin needs this hack
import "./define_froala";

import FroalaEditorComponent from "react-froala-wysiwyg";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "../Modal";
import { faUpRightAndDownLeftFromCenter } from "@fortawesome/pro-solid-svg-icons";
import { getMaxLengthMessage, getRecommendedLengthExceededMessage, getRequiredMessage } from "../../utils/validators";
import { Label } from "office-ui-fabric-react";
import { useCharCount } from "./hooks/useCharCount";
import { VersionTag } from "@/reports/common/VersionTag";
import styles from "./FroalaEditorBase.module.scss";
import { FROALA_KEY } from "./configs";
import { withTooltip } from "@/common/lib";
import { NoPptPreview } from "@/admin/common/NoPptPreview";
const IS_DEV_MODE = window.location.hostname === "localhost" && !window.Cypress;
if (!IS_DEV_MODE) {
  // This pollutes the console excessively making it very difficult to debug.
  import("../../../vendor/beyond-grammar-plugin");
}

window.FroalaEditor.RegisterShortcut(190, "formatUL", null, ".", false, false);
window.FroalaEditor.RegisterShortcut(191, "formatOL", null, "/", false, false);

const isIconVisible = (ele, container, gap): boolean => {
  const { bottom } = ele.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();

  return bottom - gap >= containerRect.top && bottom <= containerRect.bottom;
};

const FROALA_BASE_CONFIG = {
  key: FROALA_KEY,
  htmlRemoveTags: ["iframe", "script", "style"],
  attribution: false
};

interface IFroalaBaseProps {
  model: string
  config: object
  onModelChange: (val: string) => void
  className?: string
  toolbarTitle?: string
  dataTestId?: string
  required?: boolean
  label?: string
  disabled?: boolean
  versionModel?: any
  versionKey?: string
  split?: React.ReactNode
  pptSection?: string
  pptFieldName?: string
}

export const FroalaEditorBase = (
  {
    model,
    config,
    onModelChange,
    className,
    toolbarTitle,
    dataTestId,
    required,
    label,
    disabled,
    versionModel,
    versionKey,
    split,
    pptSection,
    pptFieldName
  }: IFroalaBaseProps): JSX.Element => {
  const editorRef = useRef<any>();
  const containerRef = useRef<any>();
  const iconRef = useRef<any>();

  const [editorId] = useState(uuid());
  const [currentImageContainer, setCurrentImageContainer] = useState<HTMLElement>();
  const [imageLeaveTimeoutId, setImageLeaveTimeoutId] = useState<any>();
  const [isHoverIcon, setIsHoverIcon] = useState(false);
  const [isHoverImage, setIsHoverImage] = useState(false);
  const [imageContainerRect, setImageContainerRect] = useState<DOMRect>();
  const [showImageModal, setShowImageModal] = useState(false);
  const { characterLimit, recommendedCharLimit, characterLimitAlgo, onCharLimitExceeded } = config as any;

  const {
    charLimitEnabled,
    charCountConfig,
    charCount,
    isCharLimitExceeded,
    isRecommendedCharLimitExceeded
  } = useCharCount(config, editorRef, recommendedCharLimit, characterLimit, characterLimitAlgo);

  useEffect(() => {
    $(containerRef.current).on("mouseenter", ".fr-view img", (e) => {
      clearTimeout(imageLeaveTimeoutId);
      setCurrentImageContainer(e.target);
      setImageContainerRect(e.target.getBoundingClientRect());
      setIsHoverImage(true);
    });
    $(containerRef.current).on("mouseleave", ".fr-view img", () => {
      const timeoutId = setTimeout(() => {
        setIsHoverImage(false);
      }, 100);
      setImageLeaveTimeoutId(timeoutId);
    });
  }, [containerRef.current]);

  useEffect(() => {
    const handleScroll = (): void => {
      if (iconRef?.current != null && currentImageContainer != null) {
        setImageContainerRect(currentImageContainer.getBoundingClientRect());
      }
    };

    const editorWrapper = $(containerRef.current).find("div.fr-wrapper").get(0);
    editorWrapper?.addEventListener("scroll", handleScroll);

    document.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("scroll", handleScroll);
      editorWrapper?.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef?.current, iconRef?.current]);

  const toolbarConfig = {
    toolbarContainer: `#toolbarContainer-${editorId}`
  };

  const editorConfig = {
    ...FROALA_BASE_CONFIG,
    ...config,
    ...(toolbarTitle ? toolbarConfig : {}),
    ...charCountConfig
  };

  const renderToolbarContainer = useCallback(() => {
    if (toolbarTitle) {
      return (
        <div className={styles.toolbarContainer}>
          <div className={styles.toolbarTitle}><strong>{toolbarTitle}</strong></div>
          <div className={styles.toolbarContent} id={`toolbarContainer-${editorId}`}></div>
          <div className={styles.versionTagContainer}>
            {versionModel && versionKey &&
              <VersionTag className="ml-0" model={versionModel} vKey={versionKey}
                mode="visualEditor" />}
          </div>
        </div>
      );
    } else {
      return null;
    }
  }, [toolbarTitle, versionModel, versionKey, editorId]);

  const renderExpandIcon = useCallback(() => {
    if (currentImageContainer != null && imageContainerRect != null && (isHoverIcon || isHoverImage)) {
      const rect = imageContainerRect;
      const editorWrapper = $(containerRef.current).find("div.fr-wrapper").get(0);
      const visible = isIconVisible(currentImageContainer, editorWrapper, 46);

      const parentRect = editorWrapper?.getBoundingClientRect();

      if (!parentRect) return null;

      return visible && (
        <div className={"hovered-image-icon"}
          ref={iconRef}
          style={{ bottom: parentRect.bottom - rect.bottom + 14, right: parentRect.right - rect.right + 14 }}
          onClick={() => setShowImageModal(true)}
          onMouseEnter={() => setIsHoverIcon(true)}
          onMouseLeave={() => setIsHoverIcon(false)}
          data-testid="preview-image-button"
        >
          <FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} size={"sm"} />
        </div>
      );
    } else {
      return null;
    }
  }, [currentImageContainer, imageContainerRect, isHoverImage, isHoverIcon]);

  const renderImagePreviewModal = (): React.ReactNode => {
    return (
      <Modal title="" onClose={() => setShowImageModal(false)}>
        <div className="froala-editor-image-preview" data-testid="froala-editor-image-preview">
          {currentImageContainer != null &&
            <img
              src={currentImageContainer.getAttribute("src") ?? ""}
              alt={currentImageContainer.getAttribute("alt") ?? ""}
            />
          }
        </div>
      </Modal>
    );
  };

  const errorMessages = {
    required: getRequiredMessage(label),
    limitExceeded: getMaxLengthMessage(characterLimit)
  };

  const warningMessages = {
    recommendedLimitExceeded: getRecommendedLengthExceededMessage(recommendedCharLimit)
  };

  const getErrorMessage = useCallback(() => {
    let errorName = "";
    if (required && charCount == 0) {
      errorName = "required";
    } else if (isCharLimitExceeded()) {
      errorName = "limitExceeded";
    }
    return errorMessages[errorName] || "";
  }, [required, charCount, isCharLimitExceeded]);

  const getWarningMessage = useCallback(() => {
    let warningName = "";
    if (isRecommendedCharLimitExceeded()) {
      warningName = "recommendedLimitExceeded";
    }
    return warningMessages[warningName] || "";
  }, [charCount, isRecommendedCharLimitExceeded]);

  useEffect(() => {
    if (!onCharLimitExceeded) return;
    onCharLimitExceeded(isCharLimitExceeded());
  }, [charCount]);

  const errorMessage = getErrorMessage();
  const warningMessage = getWarningMessage();
  const limitExceeded = isCharLimitExceeded();
  const recommendedCharLimitExceeded = isRecommendedCharLimitExceeded();
  const hasError = limitExceeded || errorMessage;
  const hasWarning = recommendedCharLimitExceeded && !hasError;

  const renderLabel = useCallback(() => {
    return toolbarTitle == null && label != null && <div>
      <Label className="froala-editor-title" required={required} disabled={disabled}>
        {label}
      </Label>
      {pptSection && (
        <NoPptPreview section={pptSection} fieldName={pptFieldName} />
      )}
    </div>;
  }, [label, required, disabled]);

  return (
    <div className={cn(styles.froalaEditor, "froala-editor", className, { [styles.error]: hasError })} data-testid={dataTestId} ref={containerRef}>
      {renderToolbarContainer()}
      {renderLabel()}
      <div className={cn(styles.editorContainer, { [styles.hasSplit]: !!split })}>
        <div className={styles.editorWrapper}>
          <FroalaEditorComponent model={model} config={editorConfig} onModelChange={onModelChange} ref={editorRef} />
        </div>
        {split && <div className={styles.splitWrapper}>{split}</div>}
      </div>
      {renderExpandIcon()}
      <div className={styles.footerContainer} >
        {hasError && <span className={cn(
          { // @ts-expect-error
            [styles.balancedText]: editorConfig.balanceCharLimitMessage
          }, styles.errorMessage
        )} data-testid="error-message">
          {errorMessage}
        </span>}
        {hasWarning && <span className={cn(
          { // @ts-expect-error
            [styles.balancedText]: editorConfig.balanceCharLimitMessage
          }, styles.warningMessage
        )} data-testid="warning-message">
          {warningMessage}
        </span>}
        {charLimitEnabled &&
          <span className={cn(
            styles.charCounter,
            {
              [styles.hasSplit]: !!split,
              [styles.exceeded]: limitExceeded,
              [styles.recommendedExceeded]: recommendedCharLimitExceeded
            }
          )} data-testid="char-counter">
            {`${charCount}/${characterLimit}`}
          </span>
        }
      </div>
      {showImageModal && renderImagePreviewModal()}
    </div>
  );
};
